import React, { useState } from 'react';
import { MdArrowBack } from 'react-icons/md';

// Category colors from the BeHuman accent palette (see styles/tokens.md).
// Hex literals because Recharts passes these to SVG `fill` attrs which
// don't resolve CSS vars. Keep these in sync with theme.css.
// Refreshed May 2026 to the new chart palette: green anchor + soft pastels.
const SPEND_CATS = [
  { key: 'groceries', label: 'Groceries', color: '#2FBF00' }, // td-shield (chart-1)
  { key: 'dining',    label: 'Dining',    color: '#FEBFDA' }, // td-pink   (chart-3)
  { key: 'transit',   label: 'Transit',   color: '#A4D6DD' }, // td-sky    (chart-2)
  { key: 'shopping',  label: 'Shopping',  color: '#FEEF60' }, // td-yellow (chart-4)
  { key: 'bills',     label: 'Bills',     color: '#448889' }, // td-teal   (chart-5)
];

const SPEND_DATA = {
  Week: {
    total: 287, delta: -63, deltaLabel: 'vs last week', positive: true,
    bars: [
      { label: 'Mon', dining: 0,  groceries: 45, transit: 12, shopping: 0,  bills: 0 },
      { label: 'Tue', dining: 28, groceries: 0,  transit: 6,  shopping: 15, bills: 0 },
      { label: 'Wed', dining: 14, groceries: 87, transit: 6,  shopping: 0,  bills: 0 },
      { label: 'Thu', dining: 0,  groceries: 0,  transit: 12, shopping: 22, bills: 0 },
      { label: 'Fri', dining: 45, groceries: 32, transit: 6,  shopping: 0,  bills: 0 },
      { label: 'Sat', dining: 38, groceries: 0,  transit: 0,  shopping: 65, bills: 0 },
      { label: 'Sun', dining: 22, groceries: 0,  transit: 0,  shopping: 9,  bills: 0 },
    ],
  },
  Month: {
    total: 1847, delta: 142, deltaLabel: 'vs last month', positive: false,
    bars: [
      { label: 'Wk 1', dining: 85,  groceries: 210, transit: 48, shopping: 65,  bills: 0   },
      { label: 'Wk 2', dining: 120, groceries: 185, transit: 52, shopping: 90,  bills: 412 },
      { label: 'Wk 3', dining: 95,  groceries: 230, transit: 48, shopping: 120, bills: 0   },
      { label: 'Wk 4', dining: 110, groceries: 195, transit: 52, shopping: 80,  bills: 0   },
    ],
  },
  Year: {
    total: 22340, delta: -1820, deltaLabel: 'vs last year', positive: true,
    bars: [
      { label: 'Jan', dining: 340, groceries: 820,  transit: 190, shopping: 320, bills: 410 },
      { label: 'Feb', dining: 280, groceries: 750,  transit: 180, shopping: 260, bills: 410 },
      { label: 'Mar', dining: 410, groceries: 890,  transit: 195, shopping: 440, bills: 410 },
      { label: 'Apr', dining: 370, groceries: 810,  transit: 185, shopping: 380, bills: 410 },
      { label: 'May', dining: 420, groceries: 870,  transit: 192, shopping: 460, bills: 410 },
      { label: 'Jun', dining: 450, groceries: 920,  transit: 188, shopping: 520, bills: 410 },
      { label: 'Jul', dining: 490, groceries: 850,  transit: 180, shopping: 490, bills: 410 },
      { label: 'Aug', dining: 460, groceries: 880,  transit: 185, shopping: 540, bills: 410 },
      { label: 'Sep', dining: 390, groceries: 840,  transit: 190, shopping: 410, bills: 410 },
      { label: 'Oct', dining: 430, groceries: 910,  transit: 195, shopping: 490, bills: 410 },
      { label: 'Nov', dining: 480, groceries: 960,  transit: 188, shopping: 570, bills: 410 },
      { label: 'Dec', dining: 560, groceries: 1020, transit: 192, shopping: 740, bills: 410 },
    ],
  },
};

const SpendingScreen = ({ onNavigate, onBack }) => {
  const [period, setPeriod] = useState('Month');
  const d = SPEND_DATA[period];
  const maxBarTotal = Math.max(...d.bars.map(b => SPEND_CATS.reduce((s, c) => s + (b[c.key] || 0), 0)));
  const catTotals = SPEND_CATS.map(c => ({ ...c, total: d.bars.reduce((s, b) => s + (b[c.key] || 0), 0) }));
  const grandTotal = catTotals.reduce((s, c) => s + c.total, 0);

  return (
    <div className="screen bh-flow">
      <header className="bh-flow-hero">
        <div className="bh-flow-hero-left">
          <button className="bh-flow-back" onClick={onBack || (() => onNavigate && onNavigate('insightsScreen'))} aria-label="Back">
            <MdArrowBack />
          </button>
          <h1 className="bh-flow-title">Spending Trends</h1>
        </div>
      </header>

      <div className="scroll-content gray-bg">
        <div className="st-hero">
          <p className="st-period-label">{period === 'Week' ? 'This Week' : period === 'Month' ? 'This Month' : 'This Year'}</p>
          <p className="st-total">${d.total.toLocaleString()}</p>
          <p className={`st-delta ${d.positive ? 'pos' : 'neg'}`}>
            {d.positive ? '▼' : '▲'} ${Math.abs(d.delta).toLocaleString()} {d.deltaLabel}
          </p>
        </div>

        <div className="st-toggle-row">
          {['Week', 'Month', 'Year'].map(p => (
            <button key={p} className={`st-toggle-btn${period === p ? ' active' : ''}`} onClick={() => setPeriod(p)}>{p}</button>
          ))}
        </div>

        <div className="st-chart-card">
          <div className="st-chart">
            {d.bars.map((bar, i) => {
              const total = SPEND_CATS.reduce((s, c) => s + (bar[c.key] || 0), 0);
              const pct = maxBarTotal > 0 ? (total / maxBarTotal) * 100 : 0;
              return (
                <div key={i} className="st-bar-col">
                  <div className="st-bar-track">
                    <div className="st-bar-stack" style={{ height: `${pct}%` }}>
                      {[...SPEND_CATS].reverse().map(cat => {
                        const v = bar[cat.key] || 0;
                        if (!v) return null;
                        return (
                          <div key={cat.key} className="st-bar-seg"
                            style={{ flex: v, background: cat.color }} />
                        );
                      })}
                    </div>
                  </div>
                  <span className="st-bar-label">{bar.label}</span>
                </div>
              );
            })}
          </div>
        </div>

        <div className="st-breakdown-card">
          <p className="st-breakdown-title">BREAKDOWN</p>
          {catTotals.filter(c => c.total > 0).sort((a, b) => b.total - a.total).map(cat => (
            <div key={cat.key} className="st-breakdown-row">
              <div className="st-cat-dot" style={{ background: cat.color }} />
              <span className="st-cat-label">{cat.label}</span>
              <div className="st-cat-bar-bg">
                <div className="st-cat-bar-fill" style={{ width: `${(cat.total / grandTotal) * 100}%`, background: cat.color }} />
              </div>
              <span className="st-cat-pct">{Math.round((cat.total / grandTotal) * 100)}%</span>
              <span className="st-cat-amt">${cat.total.toLocaleString()}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SpendingScreen;
